import React from 'react'
import { BrowserRouter } from 'react-router-dom'
import { Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material'
import { ThemeProvider } from './app/ThemeProvider'
import { AppRoutes } from './app/routes'
import './styles/badge.css'
import './styles/layout.css'
import { getMe, type MeResponse } from './api/me'
import { getIntegrations } from './api/integrations'
import { getInboxCounters } from './api/inbox'
import { MeProvider } from './app/MeContext'
import useUIStore from './store/ui'
import useMessagesStore from './store/messages'
import useThreadsStore from './store/threads'
import useUnreadStore from './store/unread'
import useStationListenerCountSettingsStore from './store/stationListenerCounts'
import useStationGeneralSettingsStore from './store/stationGeneralSettings'
import useUserSettingsStore from './store/userSettings'
import { getUserSettings } from './api/userSettings'
import PublicApp from './public/PublicApp'
import RealtimeCoordinator from './realtime/RealtimeCoordinator'

const App: React.FC = () => {
  const [me, setMe] = React.useState<MeResponse | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [requiresAuth, setRequiresAuth] = React.useState(false)
  const [loading, setLoading] = React.useState(true)
  const bootstrapRef = React.useRef<typeof window.__RPA_BOOTSTRAP__ | null>(
    typeof window !== 'undefined' ? window.__RPA_BOOTSTRAP__ ?? null : null,
  )
  const countersFetchState = React.useRef<Map<number, 'pending' | 'done'>>(new Map())
  const setThemePreference = useUIStore((state) => state.setThemePreference)
  const setDefaultPlatforms = useMessagesStore((state) => state.setDefaultPlatforms)
  const setThreadDefaultPlatforms = useThreadsStore((state) => state.setDefaultPlatforms)
  const setUnreadTotal = useUnreadStore((state) => state.setTotal)
  const resetListenerCountSettings = useStationListenerCountSettingsStore((state) => state.reset)
  const setManyListenerCountSettings = useStationListenerCountSettingsStore(
    (state) => state.setManyListenerCountSettings,
  )
  const resetGeneralSettings = useStationGeneralSettingsStore((state) => state.reset)
  const setManyGeneralSettings = useStationGeneralSettingsStore(
    (state) => state.setManyGeneralSettings,
  )
  const applyUserSettings = useUserSettingsStore((state) => state.applyServerSettings)
  const resetUserSettings = useUserSettingsStore((state) => state.reset)
  const publicPage = bootstrapRef.current?.page

  if (publicPage === 'pricing' || publicPage === 'yt-credits') {
    return (
      <ThemeProvider>
        <PublicApp page={publicPage} />
      </ThemeProvider>
    )
  }
  const activeStationId = React.useMemo(() => {
    const bootstrapStationId = bootstrapRef.current?.defaultStationId
    const meStationId = me?.stations?.[0]?.id

    if (typeof meStationId === 'number' && meStationId > 0) {
      return meStationId
    }

    if (typeof bootstrapStationId === 'number' && bootstrapStationId > 0) {
      return bootstrapStationId
    }

    return null
  }, [me])
  const hasStations = Boolean(me?.stations?.length)
  const handleUpdateMe = React.useCallback(
    (updater: (prev: MeResponse) => MeResponse) => {
      setMe((prev) => {
        if (!prev) {
          return prev
        }

        const next = updater(prev)
        return next
      })
    },
    [],
  )

  React.useEffect(() => {
    let cancelled = false

    const load = async () => {
      try {
        const data = await getMe()
        if (cancelled) {
          return
        }
        setMe(data)
        setRequiresAuth(false)
        setError(null)
        if (data.theme) {
          setThemePreference(data.theme)
        }
        resetListenerCountSettings()
        resetGeneralSettings()
        try {
          const settings = await getUserSettings()
          if (!cancelled) {
            applyUserSettings(settings)
          }
        } catch (error) {
          if (!cancelled) {
            resetUserSettings()
          }
        }
        if (data.stations?.length) {
          setManyListenerCountSettings(
            data.stations.map((station) => ({
              stationId: station.id,
              settings: {
                enabled:
                  Boolean(station.listener_counts?.show_listener_counts) &&
                  Boolean(station.listener_counts?.listener_count_endpoint),
                endpoint: station.listener_counts?.listener_count_endpoint ?? null,
              },
            })),
          )
          setManyGeneralSettings(
            data.stations.map((station) => ({
              stationId: station.id,
              settings: {
                autoSendQuickReplies: Boolean(station.general?.auto_send_quick_replies),
                enterToSend: Boolean(station.general?.enter_to_send),
              },
            })),
          )
        }
      } catch (err) {
        if (cancelled) {
          return
        }
        const status = (err as { status?: number })?.status
        if (status === 401 || status === 403) {
          setRequiresAuth(true)
          setError(null)
        } else {
          const message = err instanceof Error ? err.message : 'Unable to load your profile.'
          setError(message)
        }
        resetListenerCountSettings()
        resetGeneralSettings()
        resetUserSettings()
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [
    resetGeneralSettings,
    resetListenerCountSettings,
    setManyGeneralSettings,
    setManyListenerCountSettings,
    setThemePreference,
    applyUserSettings,
    resetUserSettings,
  ])

  React.useEffect(() => {
    if (!me?.stations?.length) {
      resetListenerCountSettings()
      resetGeneralSettings()
      return
    }

    resetListenerCountSettings()
    resetGeneralSettings()
    setManyListenerCountSettings(
      me.stations.map((station) => ({
        stationId: station.id,
        settings: {
          enabled:
            Boolean(station.listener_counts?.show_listener_counts) &&
            Boolean(station.listener_counts?.listener_count_endpoint),
          endpoint: station.listener_counts?.listener_count_endpoint ?? null,
        },
      })),
    )
    setManyGeneralSettings(
      me.stations.map((station) => ({
        stationId: station.id,
        settings: {
          autoSendQuickReplies: Boolean(station.general?.auto_send_quick_replies),
          enterToSend: Boolean(station.general?.enter_to_send),
        },
      })),
    )
  }, [
    me,
    resetGeneralSettings,
    resetListenerCountSettings,
    setManyGeneralSettings,
    setManyListenerCountSettings,
  ])

  React.useEffect(() => {
    const bootstrapTotal = bootstrapRef.current?.unread_total
    const bootstrapStationId = bootstrapRef.current?.defaultStationId

    if (
      typeof bootstrapTotal === 'number' &&
      Number.isFinite(bootstrapTotal) &&
      typeof bootstrapStationId === 'number' &&
      bootstrapStationId === activeStationId
    ) {
      setUnreadTotal(bootstrapTotal)
    }

    if (!activeStationId) {
      return
    }

    const status = countersFetchState.current.get(activeStationId)

    if (status === 'pending' || status === 'done') {
      return
    }

    countersFetchState.current.set(activeStationId, 'pending')
    let cancelled = false

    void (async () => {
      try {
        const response = await getInboxCounters(activeStationId)
        if (cancelled) {
          return
        }
        const value = Number(response.unread_total ?? 0)
        if (!Number.isNaN(value)) {
          setUnreadTotal(value)
        }
        countersFetchState.current.set(activeStationId, 'done')
      } catch (error) {
        countersFetchState.current.delete(activeStationId)
        if ((import.meta as any)?.env?.DEV) {
          console.error('[RPA] Failed to load inbox counters', error)
        }
      }
    })()

    return () => {
      cancelled = true
      if (countersFetchState.current.get(activeStationId) === 'pending') {
        countersFetchState.current.delete(activeStationId)
      }
    }
  }, [activeStationId, hasStations, setUnreadTotal])

  React.useEffect(() => {
    if (!me?.stations?.length) {
      return
    }

    const stationId = me.stations[0]?.id

    if (!stationId) {
      return
    }

    let cancelled = false

    const loadIntegrations = async () => {
      try {
        const response = await getIntegrations(stationId)
        if (cancelled) {
          return
        }

        const configured = response.providers
          .filter((provider) => provider.status !== 'not_configured')
          .map((provider) => provider.id)

        setDefaultPlatforms(configured)
        setThreadDefaultPlatforms(configured)
      } catch (error) {
        if (cancelled) {
          return
        }

        setDefaultPlatforms([])
        setThreadDefaultPlatforms([])

        if ((import.meta as any)?.env?.DEV) {
          console.error('[RPA] Failed to load integrations for platform defaults', error)
        }
      }
    }

    void loadIntegrations()

    return () => {
      cancelled = true
    }
  }, [me, setDefaultPlatforms, setThreadDefaultPlatforms])

  const includeSuperadmin = React.useMemo(() => {
    if (!me) {
      return false
    }
    const capabilities = new Set<string>()
    me.capabilities?.forEach((cap) => capabilities.add(cap))
    me.roles?.forEach((role) => capabilities.add(role))
    me.user.capabilities?.forEach((cap) => capabilities.add(cap))
    me.user.roles?.forEach((role) => capabilities.add(role))

    return (
      capabilities.has('superadmin') ||
      capabilities.has('rpa_superadmin') ||
      capabilities.has('manage_options')
    )
  }, [me])

  let content: React.ReactNode = null

  if (loading) {
    content = (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography color="text.secondary">Loading your workspace…</Typography>
        </Stack>
      </Box>
    )
  } else if (requiresAuth) {
    content = (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 2 }}>
        <Paper variant="outlined" sx={{ p: 4, maxWidth: 420 }}>
          <Stack spacing={2}>
            <Typography variant="h5" component="h1">
              Sign in required
            </Typography>
            <Typography color="text.secondary">
              You must be logged in to access the Radio Presenter Assistant. Please sign in and refresh this page.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button variant="contained" component="a" href="/rpa-login">
                Go to login
              </Button>
              <Button variant="outlined" component="a" href="/rpa-register">
                Create account
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>
    )
  } else if (error || !me) {
    content = (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 2 }}>
        <Paper variant="outlined" sx={{ p: 4, maxWidth: 420 }}>
          <Stack spacing={2}>
            <Typography variant="h5" component="h1">
              Unable to load RPA
            </Typography>
            <Typography color="text.secondary">
              {error ?? 'An unexpected error occurred. Please try again later.'}
            </Typography>
          </Stack>
        </Paper>
      </Box>
    )
  } else {
    content = (
      <MeProvider value={me}>
        <BrowserRouter basename="/rpa-app">
          <RealtimeCoordinator enabled={hasStations} fallbackStationId={activeStationId} />
          <AppRoutes
            me={me}
            includeSuperadmin={includeSuperadmin}
            onUpdateMe={handleUpdateMe}
          />
        </BrowserRouter>
      </MeProvider>
    )
  }

  return <ThemeProvider>{content}</ThemeProvider>
}

export default App
